import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiSearch, FiRefreshCw, FiChevronLeft, FiChevronRight,
  FiFilter, FiX, FiCalendar, FiAlertCircle, FiUsers, FiCreditCard,
} from 'react-icons/fi';
import { getAdminSubscriptions, AdminSubscriptionRow } from '../../api/admin';

/* ── constantes ──────────────────────────────────────────────────────────────── */
const PLAN_BADGE: Record<string, string> = {
  free:       'bg-gray-100 text-gray-600',
  standard:   'bg-blue-100 text-blue-700',
  entreprise: 'bg-purple-100 text-purple-700',
  business:   'bg-orange-100 text-orange-700',
};
const STATUS_BADGE: Record<string, string> = {
  active:        'bg-green-100 text-green-700',
  trialing:      'bg-yellow-100 text-yellow-700',
  trial_expired: 'bg-red-100 text-red-600',
  canceled:      'bg-gray-100 text-gray-500',
  past_due:      'bg-orange-100 text-orange-700',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Actif', trialing: 'Essai', trial_expired: 'Expiré',
  canceled: 'Annulé', past_due: 'Impayé',
};
const PLANS   = ['free', 'standard', 'entreprise', 'business'];
const STATUSES = ['active', 'trialing', 'trial_expired', 'canceled', 'past_due'];

const planLabel = (p: string | null) => p ? p.charAt(0).toUpperCase() + p.slice(1) : '—';
const fmtDate   = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

/* Compte les jours restants depuis une date */
const daysLeft = (d: string | null): number | null => {
  if (!d) return null;
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86_400_000);
};

const TrialBadge: React.FC<{ trialEnd: string | null; status: string }> = ({ trialEnd, status }) => {
  if (status !== 'trialing' && status !== 'trial_expired') return null;
  const days = daysLeft(trialEnd);
  if (days === null) return null;
  if (days > 0)
    return <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">J-{days}</span>;
  return <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">Expiré</span>;
};

/* Badge expiration pour les plans payants */
const ExpiryBadge: React.FC<{ days: number | null }> = ({ days }) => {
  if (days === null) return null;
  if (days <= 0)  return <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">Expiré</span>;
  if (days <= 7)  return <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">J-{days}</span>;
  if (days <= 30) return <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-50 text-orange-600 border border-orange-200">J-{days}</span>;
  return null;
};

/* ── composant principal ─────────────────────────────────────────────────────── */
const AdminSubscriptions: React.FC = () => {
  const [subs, setSubs]           = useState<AdminSubscriptionRow[]>([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [inputVal, setInputVal]   = useState('');
  const [planFilter, setPlanFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminSubscriptions({
        page, limit,
        search: search || undefined,
        plan:   planFilter || undefined,
        status: statusFilter || undefined,
      });
      setSubs(data.subscriptions);
      setTotal(data.total);
    } catch {
      setError('Impossible de charger les abonnements');
    } finally {
      setLoading(false);
    }
  }, [page, search, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleInputChange = (val: string) => {
    setInputVal(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(val); }, 400);
  };

  const clearSearch = () => { setInputVal(''); setSearch(''); setPage(1); };
  const resetAll = () => { clearSearch(); setPlanFilter(''); setStatusFilter(''); setPage(1); };

  const totalPages = Math.ceil(total / limit);

  /* Pills de plans sur la page courante */
  const planCounts = subs.reduce<Record<string, number>>((acc, s) => {
    acc[s.plan] = (acc[s.plan] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Abonnements</h2>
          <p className="text-sm text-gray-500 mt-0.5">{total} abonnement{total !== 1 ? 's' : ''} au total</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
          <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* ── Pills plans ── */}
      {Object.keys(planCounts).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {PLANS.filter(p => planCounts[p]).map(p => (
            <button
              key={p}
              onClick={() => { setPlanFilter(planFilter === p ? '' : p); setPage(1); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition
                ${planFilter === p
                  ? (PLAN_BADGE[p] || 'bg-gray-100 text-gray-600') + ' border-current ring-2 ring-offset-1 ring-current'
                  : (PLAN_BADGE[p] || 'bg-gray-100 text-gray-600') + ' border-transparent hover:opacity-80'}`}
            >
              {planLabel(p)} <span className="font-bold">{planCounts[p]}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Filtres ── */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input
            type="text"
            placeholder="Rechercher une organisation…"
            value={inputVal}
            onChange={e => handleInputChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {inputVal && (
            <button onClick={clearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <FiX size={14} />
            </button>
          )}
        </div>
        <div className="relative">
          <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
          <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
            className="pl-7 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 appearance-none bg-white">
            <option value="">Tous les plans</option>
            {PLANS.map(p => <option key={p} value={p}>{planLabel(p)}</option>)}
          </select>
        </div>
        <div className="relative">
          <FiFilter className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={13} />
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            className="pl-7 pr-8 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 appearance-none bg-white">
            <option value="">Tous les statuts</option>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
          </select>
        </div>
        {(planFilter || statusFilter || search) && (
          <button onClick={resetAll} className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition">
            <FiX size={13} /> Réinitialiser
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={16} /> {error}
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Organisation</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Plan</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Statut</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Cycle</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Prochain renouvellement</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Utilisateurs</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600">Créé le</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    Chargement…
                  </div>
                </td></tr>
              ) : subs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Aucun abonnement trouvé</td></tr>
              ) : subs.map(sub => (
                <React.Fragment key={sub.id}>
                  <tr
                    className="hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{sub.organization_name}</div>
                      <div className="text-xs text-gray-400 font-mono">{sub.organization_slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_BADGE[sub.plan] || 'bg-gray-100 text-gray-600'}`}>
                        {planLabel(sub.plan)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[sub.status] || 'bg-gray-100 text-gray-500'}`}>
                          {STATUS_LABEL[sub.status] || sub.status}
                        </span>
                        <TrialBadge trialEnd={sub.trial_end} status={sub.status} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 capitalize">
                      {sub.billing_cycle === 'monthly' ? 'Mensuel' : sub.billing_cycle === 'yearly' ? 'Annuel' : sub.billing_cycle || '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {sub.current_period_end ? (
                        <span className="flex items-center gap-1 flex-wrap">
                          <FiCalendar size={11} className="text-gray-400" />
                          {fmtDate(sub.current_period_end)}
                          {sub.plan === 'free'
                            ? <TrialBadge trialEnd={sub.trial_end} status={sub.status} />
                            : <ExpiryBadge days={daysLeft(sub.current_period_end)} />
                          }
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-xs text-gray-600">
                        <FiUsers size={12} className="text-gray-400" />
                        {sub.user_count ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {fmtDate(sub.created_at)}
                    </td>
                  </tr>

                  {/* ── Ligne expandée ── */}
                  {expandedId === sub.id && (
                    <tr className="bg-blue-50/40">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">ID abonnement</p>
                            <p className="font-mono text-xs text-gray-700 break-all">{sub.id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">ID organisation</p>
                            <p className="font-mono text-xs text-gray-700 break-all">{sub.organization_id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Dernière mise à jour</p>
                            <p className="flex items-center gap-1.5 text-gray-700 text-xs">
                              <FiCalendar size={12} className="text-gray-400" />
                              {fmtDate(sub.updated_at)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-0.5">Fin de période d'essai</p>
                            <p className="flex items-center gap-1.5 text-gray-700 text-xs">
                              {sub.trial_end ? (
                                <>
                                  <FiCalendar size={12} className="text-gray-400" />
                                  {fmtDate(sub.trial_end)}
                                  <TrialBadge trialEnd={sub.trial_end} status={sub.status} />
                                </>
                              ) : '—'}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <span className="text-xs text-gray-500">
              {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} sur {total}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(1)} disabled={page === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">«</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">
                <FiChevronLeft size={14} /></button>
              <span className="px-3 py-1 text-xs text-gray-600">Page {page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">
                <FiChevronRight size={14} /></button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-40 transition">»</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Légende statuts ── */}
      <div className="mt-4 flex flex-wrap gap-3">
        {STATUSES.map(s => (
          <div key={s} className="flex items-center gap-1.5">
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_BADGE[s] || 'bg-gray-100 text-gray-500'}`}>
              {STATUS_LABEL[s] || s}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminSubscriptions;
